import { service } from '@ember/service'
import { t, type IntlService } from 'ember-intl'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import StatBox from 'teacher-ui-client/components/giraffe/stat-box'
import { themeKeys } from 'teacher-ui-client/components/giraffe/stat-box/themes'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import Component from '@glimmer/component'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { Product } from 'teacher-ui-client/utils/products'
import { type BaseReport, type BaseReportMetaStandardsSet } from 'teacher-ui-client/services/reporting'
import type { DistributionBands } from 'teacher-ui-client/components/reporting/performance-overview'

export interface StandardsOverviewReportData extends BaseReport {
  meta: BaseReportMetaStandardsSet
  overview: {
    average_lesson_performance: Nullable<DistributionBands>
    average_lessons_completed_per_student: Nullable<number>
    average_score_across_standards: Nullable<number>
    standards_practiced: number
    total_lessons_completed: number
    total_students: number
  }
}

interface Signature {
  Args: { reportData?: StandardsOverviewReportData; isReloading?: boolean; product?: Product }
}

export class StandardsOverview extends Component<Signature> {
  @service intl!: IntlService
  @service subscriptionType!: SubscriptionTypeService

  get isFastPhonics() {
    return this.args.product === Product.FP
  }

  get themeKey() {
    switch (this.subscriptionType.current) {
      case SubscriptionType.Reading:
        return themeKeys.THEME_OCEANY_BLUE
      case SubscriptionType.Maths:
        return themeKeys.THEME_FOREST_GREEN
      default:
        return
    }
  }

  get statBoxes() {
    return [
      {
        title: this.intl.t('reporting.statBoxes.standardsPerformance.standardsResults.standardsPracticed'),
        stats: [[{ name: '', value: formatValue(this.args.reportData?.overview.standards_practiced) }]],
      },
      {
        title: this.isFastPhonics
          ? this.intl.t('reporting.statBoxes.standardsPerformance.standardsResults.averageLessonsPerStudentFp')
          : this.intl.t('reporting.statBoxes.standardsPerformance.standardsResults.averageLessonsPerStudent'),
        stats: [
          [{ name: '', value: formatValue(this.args.reportData?.overview.average_lessons_completed_per_student) }],
        ],
      },
      {
        title: this.intl.t('reporting.statBoxes.standardsPerformance.standardsResults.averageScoreStandards'),
        stats: [
          [
            {
              name: '',
              value: formatValue(this.args.reportData?.overview.average_score_across_standards, { append: '%' }),
            },
          ],
        ],
      },
    ]
  }

  get standardsSetName() {
    return this.args.reportData?.meta.standards_set_name
  }
  <template>
    <ContentTile data-test-standards-overview class="space-y-4 p-6 {{if @isReloading 'opacity-50'}}">

      <div class="flex items-center justify-between">
        <h4>{{t "reporting.standardsPerformance.standardsResults"}}</h4>
        <span class="print:hidden">
          <PhosphorIcon @icon="phosphor-info-fill" />
          <Tooltip @text={{t "reporting.tooltips.standardsPerformance.standardsResults"}} />
        </span>
      </div>

      <div>{{this.standardsSetName}}</div>

      <div class="flex w-full gap-px" data-test-stat-box-container>
        {{#each this.statBoxes as |statBox|}}
          <StatBox class="w-1/3" @title={{statBox.title}} @theme={{this.themeKey}} @stats={{statBox.stats}} />
        {{/each}}
      </div>

    </ContentTile>
  </template>
}
